import { ChangeEvent } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  Box,
  Button,
  FormLabel,
  IconButton,
  SelectChangeEvent,
} from '@mui/material';
import z from 'zod';
import { ResponsiveFormRow } from '@ldt/features/orders';
import { FormSectionCard } from '@ldt/features/orders/components/form_section_card';
import { NccnInfoTooltip } from '@ldt/features/orders/components/nccn_info_tooltip';
import InfoIcon from 'src/assets/info_icon.svg';
import { DecimalInput } from 'src/components/decimal_input';
import { IntegerInput } from 'src/components/integer_input';
import { RadioGroupField } from 'src/components/radio_group';
import { SectionHeader } from 'src/components/section_header';
import { SelectList } from 'src/components/select_list';
import {
  GleasonScoreFields,
  OPTIONAL_GLEASON_SCORE_SCHEMA,
} from 'src/distribution_platforms/features/components/gleason_score_form_fields';
import {
  getProstateBiopsyTStageOptions,
  getProstateBiopsyTStages,
} from 'src/distribution_platforms/features/order_form';
import { NccnRiskCategory, TStage } from 'src/types';
import { NccnRiskCategoryUtils } from 'src/utils';

const NCCN_RISK_CATEGORY_VALUES = [
  NccnRiskCategory.VeryLow,
  NccnRiskCategory.Low,
  NccnRiskCategory.IntermediateFavorable,
  NccnRiskCategory.IntermediateUnfavorable,
  NccnRiskCategory.High,
  NccnRiskCategory.VeryHigh,
] as const;

const toNccnOptions = (categories: readonly NccnRiskCategory[]) =>
  categories.map((category) => ({
    value: category,
    name: NccnRiskCategoryUtils.getNccnRiskCategoryName(category),
  }));

const NCCN_RISK_CATEGORY_OPTIONS = toNccnOptions(NCCN_RISK_CATEGORY_VALUES);

const NCCN_RISK_CATEGORY_OPTIONS_WITH_NOT_PROVIDED = toNccnOptions([
  NccnRiskCategory.NotProvided,
  ...NCCN_RISK_CATEGORY_VALUES,
]);

/**
 * Soft-clinical / QC rules for unset clinical values.
 *
 * "include*" controls whether Not Provided can appear in the Zod enum so an
 * existing saved value still parses. "accept*" controls whether that unset
 * value is valid for the current submit context.
 */
export type ProstateBiopsyClinicalNotProvidedPolicy = {
  /**
   * Include T Stage Not Provided in the list of T Stage options.
   */
  includeTStageNotProvided?: boolean;
  /**
   * Allow submitting T Stage as Not Provided. True means Not Provided is valid.
   */
  acceptTStageNotProvided?: boolean;
  /**
   * Allow submitting PSA as null. True means null is valid.
   */
  acceptUnsetPsa?: boolean;
  /**
   * Allow submitting NCCN as Not Provided. True means Not Provided is valid.
   */
  acceptNccnNotProvided?: boolean;
};

export const ProstateBiopsyClinicalInformationFormData = (
  isCSOrSalesUser: boolean,
  minPsa: number,
  {
    includeTStageNotProvided = false,
    acceptTStageNotProvided = false,
    acceptUnsetPsa = false,
    acceptNccnNotProvided = false,
  }: ProstateBiopsyClinicalNotProvidedPolicy = {},
) =>
  z.object({
    primaryGleasonScore: isCSOrSalesUser
      ? z.enum(['3', '4', '5'], {
          error: 'Primary Gleason Score is required',
        })
      : OPTIONAL_GLEASON_SCORE_SCHEMA,
    secondaryGleasonScore: isCSOrSalesUser
      ? z.enum(['3', '4', '5'], {
          error: 'Secondary Gleason Score is required',
        })
      : OPTIONAL_GLEASON_SCORE_SCHEMA,
    baselinePsa: z
      .number({
        error: (issue) => {
          if (issue.code === 'invalid_type') {
            return { message: 'PSA level is required' };
          }
          return null;
        },
      })
      .min(minPsa, `PSA must be at least ${minPsa} ng/mL`)
      .nullable()
      .superRefine((val, ctx) => {
        if (!acceptUnsetPsa && val === null) {
          ctx.addIssue({ code: 'custom', message: 'PSA level is required' });
        }
      }),
    tStage: z
      .enum(
        getProstateBiopsyTStages(isCSOrSalesUser, includeTStageNotProvided),
        {
          error: 'T-Stage is required',
        },
      )
      .superRefine((val, ctx) => {
        if (!acceptTStageNotProvided && val === TStage.NotProvided) {
          ctx.addIssue({ code: 'custom', message: 'T-Stage is required' });
        }
      }),
    // Total Cores and Positive Cores — always in the schema so the type is
    // unambiguous; validation is the same regardless of user type (optional).
    // The UI conditionally renders these fields for CS/Sales users only.
    totalCores: z
      .number({
        message: 'Total Cores must be a number',
      })
      .int('Total Cores must be an integer')
      .positive('Total Cores must be a positive integer')
      .nullish(),
    positiveCores: z
      .number({
        message: 'Positive Cores must be a number',
      })
      .int('Positive Cores must be an integer')
      .positive('Positive Cores must be greater than 0')
      .nullish(),
    nccnRiskCategory: isCSOrSalesUser
      ? z
          .enum(Object.values(NccnRiskCategory), {
            error: 'NCCN Risk Category is required',
          })
          .superRefine((val, ctx) => {
            if (
              !acceptNccnNotProvided &&
              val === NccnRiskCategory.NotProvided
            ) {
              ctx.addIssue({
                code: 'custom',
                message: 'NCCN Risk Category is required',
              });
            }
          })
      : z.enum(Object.values(NccnRiskCategory)).optional().or(z.literal('')),
  });

type ProstateBiopsyClinicalInformationFormData = z.infer<
  ReturnType<typeof ProstateBiopsyClinicalInformationFormData>
>;

export const ProstateBiopsyClinicalInformationFormSection = ({
  minPsa,
  isCSOrSalesUser,
  /** Show T Stage Not Provided in the radio options. */
  showTStageNotProvided = false,
  /** Show NCCN Not Provided in the radio options. */
  showNccnNotProvided = false,
  /**
   * When true, PSA may be left unset.
   */
  acceptUnsetPsa = false,
  canCalculateNccn,
  warningsRef,
  onPrimaryGleasonScoreChange,
  onSecondaryGleasonScoreChange,
  onBaselinePsaChange,
  onTStageChange,
  onPositiveCoresChange,
  onTotalCoresChange,
  onNccnRiskCategoryChange,
  handleNccnRiskCalculation,
}: {
  minPsa: number;
  isCSOrSalesUser: boolean;
  showTStageNotProvided?: boolean;
  showNccnNotProvided?: boolean;
  acceptUnsetPsa?: boolean;
  canCalculateNccn: boolean;
  warningsRef: {
    current: {
      baselinePsa?: string;
      nccnRiskCategory?: string;
    };
  };
  onPrimaryGleasonScoreChange: (
    event: ChangeEvent<HTMLInputElement>,
  ) => void | Promise<void>;
  onSecondaryGleasonScoreChange: (
    event: ChangeEvent<HTMLInputElement>,
  ) => void | Promise<void>;
  onBaselinePsaChange: (value: number | null) => void | Promise<void>;
  onTStageChange: (
    event: ChangeEvent<HTMLInputElement> | SelectChangeEvent<string>,
  ) => void | Promise<void>;
  onPositiveCoresChange: (value: number | null) => void | Promise<void>;
  onTotalCoresChange: (value: number | null) => void | Promise<void>;
  onNccnRiskCategoryChange: (
    event: ChangeEvent<HTMLInputElement>,
  ) => void | Promise<void>;
  handleNccnRiskCalculation: () => void | Promise<void>;
}) => {
  const {
    control,
    formState: { errors },
    trigger,
  } = useFormContext<ProstateBiopsyClinicalInformationFormData>();

  const tStageOptions = getProstateBiopsyTStageOptions(
    isCSOrSalesUser,
    showTStageNotProvided,
  );

  return (
    <FormSectionCard
      header={<SectionHeader title='Clinical Information' />}
      sx={{ borderBottom: 'none' }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {/* Gleason Scores Row (CS/Sales only) */}
        {isCSOrSalesUser && (
          <GleasonScoreFields
            control={control}
            errors={errors}
            trigger={trigger}
            onPrimaryChange={onPrimaryGleasonScoreChange}
            onSecondaryChange={onSecondaryGleasonScoreChange}
          />
        )}

        {/* PSA and T-Stage Row */}
        <ResponsiveFormRow>
          <Box
            sx={{
              flex: 1,
            }}
          >
            <Controller
              name='baselinePsa'
              control={control}
              render={({ field }) => {
                const hasError = !!errors.baselinePsa;
                // Get warning from resolver (computed in resolver)
                const warningText = warningsRef.current.baselinePsa;

                return (
                  <DecimalInput
                    required={!acceptUnsetPsa}
                    fullWidth
                    label='Most Recent Pre-Biopsy PSA (ng/mL)'
                    value={field.value}
                    decimalPlaces={2}
                    min={minPsa}
                    error={hasError}
                    validationText={errors.baselinePsa?.message}
                    warningText={warningText}
                    onBlur={field.onBlur}
                    onChange={async (value) => {
                      field.onChange(value);
                      await onBaselinePsaChange(value);
                    }}
                  />
                );
              }}
            />
          </Box>
          <Box
            data-testid='clinical-t-stage'
            sx={{
              flex: 1,
            }}
          >
            <Controller
              name='tStage'
              control={control}
              render={({ field }) => (
                <SelectList
                  {...field}
                  required
                  label='Clinical T-stage'
                  labelId='t-stage-label'
                  value={field.value}
                  options={tStageOptions}
                  validationText={errors.tStage?.message ?? ''}
                  onChange={async (event) => {
                    field.onChange(event);
                    trigger?.('tStage');
                    await onTStageChange(event);
                  }}
                />
              )}
            />
          </Box>
        </ResponsiveFormRow>

        {/* Total Cores and Positive Cores (CS/Sales users only) */}
        {isCSOrSalesUser && (
          <ResponsiveFormRow>
            <Box
              sx={{
                flex: 1,
              }}
            >
              <Controller
                name='positiveCores'
                control={control}
                render={({ field }) => (
                  <IntegerInput
                    fullWidth
                    label='Positive Cores'
                    value={field.value ?? null}
                    min={1}
                    error={!!errors.positiveCores}
                    helperText={errors.positiveCores?.message ?? ' '}
                    onBlur={field.onBlur}
                    onChange={(value) => {
                      field.onChange(value);
                      onPositiveCoresChange(value);
                    }}
                  />
                )}
              />
            </Box>
            <Box
              sx={{
                flex: 1,
              }}
            >
              <Controller
                name='totalCores'
                control={control}
                render={({ field }) => (
                  <IntegerInput
                    fullWidth
                    label='Total Cores'
                    value={field.value ?? null}
                    min={1}
                    error={!!errors.totalCores}
                    helperText={errors.totalCores?.message ?? ' '}
                    onBlur={field.onBlur}
                    onChange={(value) => {
                      field.onChange(value);
                      onTotalCoresChange(value);
                    }}
                  />
                )}
              />
            </Box>
          </ResponsiveFormRow>
        )}

        {/* NCCN Risk Category */}
        {isCSOrSalesUser && (
          <Controller
            name='nccnRiskCategory'
            control={control}
            render={({ field }) => {
              // Get warning from resolver (computed in resolver)
              const nccnWarning = warningsRef.current.nccnRiskCategory;

              const hasError = !!errors.nccnRiskCategory;

              return (
                <Box data-testid='clinical-nccn-risk-category'>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <FormLabel error={hasError} required>
                      NCCN Risk Category
                    </FormLabel>
                    <NccnInfoTooltip>
                      <IconButton
                        size='small'
                        aria-label='View NCCN Risk Category Guidelines'
                        sx={{ padding: '4px' }}
                      >
                        <img
                          src={InfoIcon}
                          alt='Info'
                          style={{ width: '16px', height: '16px' }}
                        />
                      </IconButton>
                    </NccnInfoTooltip>
                    <Button
                      size='small'
                      variant='contained'
                      onClick={handleNccnRiskCalculation}
                      disabled={!canCalculateNccn}
                      aria-label='Calculate NCCN Risk Category'
                    >
                      CALCULATE
                    </Button>
                  </Box>
                  <RadioGroupField
                    {...field}
                    required
                    row
                    name={field.name}
                    label='' // Label is now handled above
                    value={field.value}
                    options={
                      showNccnNotProvided
                        ? NCCN_RISK_CATEGORY_OPTIONS_WITH_NOT_PROVIDED
                        : NCCN_RISK_CATEGORY_OPTIONS
                    }
                    validationText={errors.nccnRiskCategory?.message ?? ''}
                    warningText={nccnWarning || undefined}
                    onChange={async (event) => {
                      field.onChange(event);
                      await onNccnRiskCategoryChange(event);
                    }}
                  />
                </Box>
              );
            }}
          />
        )}
      </Box>
    </FormSectionCard>
  );
};
